// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
/**
 * Verticals Configuration Registry
 * Central registry mapping industry verticals to their pages, nav items, and role permissions.
 * Adding a new vertical = adding a new entry here + creating its page files.
 */

export const VERTICALS = {
  electrical: {
    id: 'electrical',
    name: 'Electrical',
    icon: '\u26A1',
    color: '#3B82F6',
    description: 'Electrical contracting, panel upgrades, wiring, code compliance, EV chargers',
    pages: {
      dashboard: { path: '/electrical', label: 'Electrical Ops', icon: '\u26A1' },
      sops:      { path: '/electrical/sops', label: 'Electrical SOPs', icon: '\uD83D\uDCD6' },
      panels:    { path: '/electrical/panels', label: 'Panel Schedules', icon: '\uD83D\uDD0C' },
      permits:   { path: '/electrical/permits', label: 'Permits', icon: '\uD83D\uDCCB' },
      code:      { path: '/electrical/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/electrical/loadcalc', label: 'Load Calculator', icon: '\uD83D\uDD22' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'panels', 'permits', 'code', 'calculator'],
      dispatcher: ['dashboard', 'permits'],
      technician: ['sops', 'code', 'calculator'],
    },
  },

  hvac: {
    id: 'hvac',
    name: 'HVAC',
    icon: '\uD83C\uDF21\uFE0F',
    color: '#0891B2',
    description: 'Heating, ventilation, air conditioning, refrigeration, gas fitting',
    pages: {
      dashboard: { path: '/hvac', label: 'HVAC Ops', icon: '\uD83C\uDF21\uFE0F' },
      sops:      { path: '/hvac/sops', label: 'HVAC SOPs', icon: '\uD83D\uDCD6' },
      code:      { path: '/hvac/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/hvac/calculator', label: 'Load Calculator', icon: '\uD83D\uDD22' },
      permits:   { path: '/hvac/permits', label: 'Permits & Inspections', icon: '\uD83D\uDCCB' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'code', 'calculator', 'permits'],
      dispatcher: ['dashboard', 'permits'],
      technician: ['sops', 'code', 'calculator'],
    },
  },

  plumbing: {
    id: 'plumbing',
    name: 'Plumbing',
    icon: '\uD83D\uDEB0',
    color: '#2563EB',
    description: 'Plumbing, gas fitting, drain cleaning, backflow prevention, water systems',
    pages: {
      dashboard: { path: '/plumbing', label: 'Plumbing Ops', icon: '\uD83D\uDEB0' },
      sops:      { path: '/plumbing/sops', label: 'Plumbing SOPs', icon: '\uD83D\uDCD6' },
      code:      { path: '/plumbing/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/plumbing/calculator', label: 'Pipe Sizing', icon: '\uD83D\uDD22' },
      permits:   { path: '/plumbing/permits', label: 'Permits & Inspections', icon: '\uD83D\uDCCB' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'code', 'calculator', 'permits'],
      dispatcher: ['dashboard', 'permits'],
      technician: ['sops', 'code', 'calculator'],
    },
  },

  concrete: {
    id: 'concrete',
    name: 'Concrete',
    icon: '\uD83E\uDDF1',
    color: '#78716C',
    description: 'Concrete pouring, foundations, flatwork, formed walls, testing, finishing',
    pages: {
      dashboard: { path: '/concrete', label: 'Concrete Ops', icon: '\uD83E\uDDF1' },
      sops:      { path: '/concrete/sops', label: 'Concrete SOPs', icon: '\uD83D\uDCD6' },
      code:      { path: '/concrete/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/concrete/calculator', label: 'Volume Calculator', icon: '\uD83D\uDD22' },
      permits:   { path: '/concrete/permits', label: 'Inspections & Testing', icon: '\uD83D\uDCCB' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'code', 'calculator', 'permits'],
      dispatcher: ['dashboard', 'permits'],
      technician: ['sops', 'code', 'calculator'],
    },
  },

  construction: {
    id: 'construction',
    name: 'Construction',
    icon: '\uD83D\uDEA7',
    color: '#D97706',
    description: 'General construction, framing, roofing, siding, building envelope, safety',
    pages: {
      dashboard: { path: '/construction', label: 'Construction Ops', icon: '\uD83D\uDEA7' },
      sops:      { path: '/construction/sops', label: 'Construction SOPs', icon: '\uD83D\uDCD6' },
      code:      { path: '/construction/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/construction/calculator', label: 'Material Estimator', icon: '\uD83D\uDD22' },
      permits:   { path: '/construction/permits', label: 'Building Permits', icon: '\uD83D\uDCCB' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'code', 'calculator', 'permits'],
      dispatcher: ['dashboard', 'permits'],
      technician: ['sops', 'code', 'calculator'],
    },
  },

  overheaddoors: {
    id: 'overheaddoors',
    name: 'Overhead Doors',
    icon: '\uD83D\uDEAA',
    color: '#EF4444',
    description: 'Overhead door installation, repair, springs, openers, dock equipment, fire-rated doors',
    pages: {
      dashboard: { path: '/overheaddoors', label: 'Overhead Doors Ops', icon: '\uD83D\uDEAA' },
      sops:      { path: '/overheaddoors/sops', label: 'Door SOPs', icon: '\uD83D\uDCD6' },
      code:      { path: '/overheaddoors/code', label: 'Code Reference', icon: '\uD83D\uDCDC' },
      calculator:{ path: '/overheaddoors/calculator', label: 'Door Calculator', icon: '\uD83D\uDD22' },
      springs:   { path: '/spring-engineering', label: 'Spring Engineering', icon: '\u2699\uFE0F' },
      permits:   { path: '/overheaddoors/permits', label: 'Permits & Safety', icon: '\uD83D\uDCCB' },
    },
    navByRole: {
      owner:      ['dashboard', 'sops', 'code', 'calculator', 'springs', 'permits'],
      dispatcher: ['dashboard', 'springs', 'permits'],
      technician: ['sops', 'code', 'calculator', 'springs'],
    },
  },
};

// Roles that can see vertical-specific nav items
export const VERTICAL_AWARE_ROLES = ['owner', 'dispatcher', 'technician'];

// Default verticals for demo users
export const DEMO_VERTICALS = ['electrical'];
